/**
 * BOF-76 — Charging Station Dashboard.
 *
 * Mirrors the fleet endpoints in `specs/010-charging-station-dashboard/contracts/dashboard-api.md`.
 *
 * Two fields carry an *absence* that the screen must render as an explanation rather than a zero:
 * `census: null` (no charge-point snapshot was taken for this period) and `splitAvailable: false`
 * (the days behind this period were computed before power types were known). `tsconfig` has
 * `strict: false` and no `strictNullChecks`, so neither is enforced by the compiler — every consumer
 * checks them explicitly, and the tests assert that they do.
 *
 * Energy is kWh throughout, as the API returns it. MWh is a presentation choice, never a stored or
 * transported unit. Money is integer cents.
 */

/** How a session's connector is classified. `UNCLASSIFIED` is never guessed into AC or DC. */
export type PowerClass = "AC" | "DC" | "UNCLASSIFIED";

export type Granularity = "WEEK" | "MONTH" | "MONTH_ADJUSTED";

export interface SessionFigures {
  total: number;
  paid: number;
  free: number;
  ac: number;
  dc: number;
  unclassified: number;
  /** Sessions that delivered energy — the denominator of every per-session average. */
  qualifying: number;
  /** Per power class, so each class's per-session average excludes its own zero-energy sessions. */
  qualifyingAc: number;
  qualifyingDc: number;
  qualifyingUnclassified: number;
}

export interface EnergyFigures {
  total: number;
  ac: number;
  dc: number;
  unclassified: number;
  paid: number;
  free: number;
}

/**
 * How many charge points existed and how many were online. A state, not an activity: it is
 * snapshotted per day and reported at a period's end, never summed across the period's days.
 */
export interface Census {
  total: number;
  online: number;
  /** The last day in the period that carried a snapshot. */
  asOf: string;
  /** Counted nightly, or imported from the workbook's hand-kept history. */
  source: CensusSource;
}

/**
 * `SNAPSHOT`: counted by the nightly run. `IMPORTED`: the workbook's hand-kept monthly figure, imported
 * as the month's end-of-month standing for the months before snapshots existed.
 */
export type CensusSource = "SNAPSHOT" | "IMPORTED";

/** One stored day, as `GET /api/v1/analytics/fleet/days` returns it. */
export interface FleetDayRow {
  date: string;
  sessions: number;
  paidSessions: number;
  freeSessions: number;
  zeroEnergySessions: number;
  acSessions: number;
  dcSessions: number;
  unclassifiedSessions: number;
  energyKwh: number;
  acEnergyKwh: number;
  dcEnergyKwh: number;
  unclassifiedEnergyKwh: number;
  paidEnergyKwh: number;
  freeEnergyKwh: number;
  moneyCents: number;
  cochargeMoneyCents: number;
  /** null where no snapshot was taken that day — distinct from zero charge points. */
  chargePointsTotal: number | null;
  chargePointsOnline: number | null;
  censusAt: string | null;
  censusSource: CensusSource | null;
  splitAvailable: boolean;
  computedAt: string | null;
}

export interface FleetDaysResponse {
  from: string;
  to: string;
  days: FleetDayRow[];
  /** Dates in range with no stored row. Never silently omitted — a gap understates a total. */
  missingDays: string[];
  /** The earliest day *in this range* that carries a census — where the census line may start. */
  censusFrom: string | null;
}

/** One period, summed from its days by the service. */
export interface FleetPeriod {
  /** `2026-W38` or `2026-09`. */
  key: string;
  label: string;
  /** Inclusive. */
  from: string;
  to: string;
  daysInPeriod: number;
  daysElapsed: number;
  complete: boolean;
  /** True when activity has been scaled to a 30-day basis. The census never is. */
  adjusted: boolean;
  /** The multiplier applied to activity when `adjusted`; null otherwise. */
  adjustmentFactor: number | null;

  sessions: SessionFigures;
  energyKwh: EnergyFigures;
  census: Census | null;
  moneyCents: number;
  cochargeMoneyCents: number;

  splitAvailable: boolean;
  /** True when some day in the range had no stored row, so the totals are understated. */
  partial: boolean;
  /** The finished days in this period with no stored row. */
  missingDays: string[];
  computedAt: string | null;
  previousKey: string | null;
}

export interface FleetPeriodsResponse {
  granularity: Granularity;
  periods: FleetPeriod[];
}

// ── KPI scorecard ────────────────────────────────────────────────────────────

/** Operational sub-score keys, in the order the model weighs them. */
export type OperationalTerm = "activation" | "cpOnlinePerClient" | "cpTotalPerClient" | "clientGrowth";

/** Monetization sub-score keys, in the order the model weighs them. */
export type MonetizationTerm = "feeGrowth" | "feePerClient" | "feePerCpTotal";

export interface ScoreDetail {
  value: number;
  /**
   * Pre-weight ratios against target, so a reader can see which term moved the score. 1.0 is exactly
   * on target; above 1.0 is legitimate — the bonus above target is soft, not capped.
   */
  subScores: Record<string, number>;
  /** Points subtracted after weighting: churn for Operational, fee decline for Monetization. */
  penalty: number;
}

export interface KpiScores {
  operational: ScoreDetail | null;
  monetization: ScoreDetail | null;
  overall: number | null;
}

/**
 * What one month's scores were computed from (FR-050). Every field is null where it cannot be
 * computed — never zero — so the screen says "not available" instead of showing a number.
 */
export interface ScoreInputs {
  activationRate: number | null;
  cpOnlinePerClient: number | null;
  cpTotalPerClient: number | null;
  clientGrowth: number | null;
  netNewClients: number | null;
  previousClients: number | null;
  feeGrowth: number | null;
  /** Cents per client. */
  feePerClient: number | null;
  /** Cents per charge point (total). */
  feePerCpTotal: number | null;
  /** Mean of the trailing months that have a value, in cents. */
  feePerClientBaseline: number | null;
  feePerCpTotalBaseline: number | null;
}

export interface KpiMonth {
  /** `YYYY-MM`. */
  month: string;
  /** False for the running month. */
  complete: boolean;
  /** Some day of the month has no stored row, so the fee and sessions are understated. */
  partial: boolean;
  /** The month's service fee; see `serviceFeeSource` for where it comes from. */
  serviceFeeCents: number | null;
  /**
   * `INVOICED`: cocharge's commission on the month's completed credit-note run — the fee as billed.
   * `IMPORTED`: the workbook's hand-kept figure (9–12% below the billed fee where both exist). `ESTIMATED`:
   * the configured share of the month's session money, for a month not invoiced yet (within 0.3% of the
   * billed fee where both exist).
   */
  serviceFeeSource: FeeSource | null;
  /** null when any month of the year so far has no stored day. */
  serviceFeeYtdCents: number | null;
  feeChangeCents: number | null;
  /** null where the month's credit-note run has not completed — not zero clients (FR-045). */
  invoicedClients: number | null;
  /** `BILLING` (the month's credit-note run) or `IMPORTED` (the workbook). */
  invoicedClientsSource: "BILLING" | "IMPORTED" | null;
  /** Distinct customers on the month's credit-note run. */
  distinctCustomers: number | null;
  /** Credit notes on the month's run — one per customer and station. */
  creditNotes: number | null;
  chargePointsOnline: number | null;
  chargePointsTotal: number | null;
  /** Whether the charge-point figures were counted nightly or imported from the workbook. */
  chargePointsSource: CensusSource | null;
  paidSessions: number | null;
  /** Present only in a month where the client count fell; absent is not zero (FR-046). */
  churnRate: number | null;
  /** False when any score input is missing; both scores are then null (FR-052). */
  scoreInputsAvailable: boolean;
  inputs: ScoreInputs;
  scores: KpiScores;
}

/**
 * The score model as the server holds it. Returned so the screen can state the model on the page
 * without keeping a copy that drifts from the one actually used (FR-051).
 */
export interface ScoreParameters {
  operationalTargets: Record<OperationalTerm, number>;
  operationalWeights: Record<OperationalTerm, number>;
  feeGrowthTarget: number;
  monetizationWeights: Record<MonetizationTerm, number>;
  /** Months the per-client and per-charge-point baselines average over. */
  baselineMonths: number;
  excessBonusRate: number;
  churnPenaltyMax: number;
  churnPenaltySlope: number;
  declinePenaltyMax: number;
  declinePenaltySlope: number;
  overallBlend: { operational: number; monetization: number; imbalancePenalty: number };
  revisedAt: string;
  /** What the scores count as an invoiced client. */
  clientBasis: ClientBasis;
}

export type FeeSource = "INVOICED" | "IMPORTED" | "ESTIMATED";

/** `CUSTOMERS`: distinct customers invoiced. `CREDIT_NOTES`: credit notes, one per customer and station. */
export type ClientBasis = "CUSTOMERS" | "CREDIT_NOTES";

export interface FleetKpiResponse {
  months: KpiMonth[];
  parameters: ScoreParameters;
}

// ── Charge-point history import ──────────────────────────────────────────────

export interface CensusImportMonth {
  /** `YYYY-MM`. */
  month: string;
  total: number;
  online: number;
}

export interface KpiHistoryImportMonth {
  month: string;
  invoicedClients: number | null;
  serviceFeeCents: number | null;
}

export type KpiHistoryImportOutcome = "IMPORTED" | "REPLACED_IMPORT" | "SKIPPED_BILLING_EXISTS" | "SKIPPED_NOT_COMPLETE" | "REJECTED_INVALID";

export interface KpiHistoryImportResponse {
  dryRun: boolean;
  written: number;
  months: Array<{ month: string; outcome: KpiHistoryImportOutcome; detail: string | null }>;
}

export type CensusImportOutcome =
  | "IMPORTED"
  | "REPLACED_IMPORT"
  | "SKIPPED_SNAPSHOT_EXISTS"
  | "SKIPPED_NOT_COMPLETE"
  | "SKIPPED_DAY_NOT_COMPUTED"
  | "REJECTED_INVALID";

export interface CensusImportResponse {
  dryRun: boolean;
  /** Months actually written; always 0 for a dry run. */
  written: number;
  months: Array<{ month: string; outcome: CensusImportOutcome; detail: string | null }>;
}

/** What a charge-point sync (the projection seed) read from station-ops. */
export interface ProjectionSeedResponse {
  /** Charge points written. */
  seeded: number;
  withPowerType: number;
  /** Charge points station-ops holds no power type for — their sessions stay unclassified. */
  unclassified: number;
  /** Rows left alone because an event newer than the sync had already updated them. */
  skippedNewer: number;
}

export interface FleetBackfillResponse {
  daysWritten: number;
  censusAvailableFrom: string | null;
  note?: string;
}

/**
 * The result shape every function in `services/clearing/fleet-dashboard` returns.
 *
 * Mirrors `services/clearing/station-performance.js`: these never throw, and `neverComputed` is kept
 * distinct from a genuine failure because "never ran" and "nothing happened" are different facts
 * with different remedies.
 */
export interface FleetFailure {
  success: false;
  neverComputed: boolean;
  message: string;
  months?: string[];
  hint?: string | null;
}

export type FleetResult<T> = { success: true; data: T } | FleetFailure;

/**
 * Narrows a result to its failure branch.
 *
 * `tsconfig` has `strict: false`, and without `strictNullChecks` TypeScript does not narrow this union
 * on `!result.success`, so `result.message` fails to compile. A type guard narrows in either mode.
 */
export const isFleetFailure = <T>(result: FleetResult<T>): result is FleetFailure => result.success === false;
